import React, { useState, useEffect } from 'react';
import { Navigation, CheckCircle, Clock, MapPin, Package, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const DriverInterface = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({
    status: '',
    location: '',
    notes: ''
  });

  const statusOptions = [
    { value: 'assigned_loading', label: 'Loading Vehicle', description: 'Starting to load items' },
    { value: 'in_transit_to_project', label: 'Heading to Project', description: 'On the way to project site' },
    { value: 'at_project_site', label: 'Arrived at Project', description: 'Waiting for delivery confirmation' },
    { value: 'in_transit_to_warehouse', label: 'Returning to Warehouse', description: 'Heading back after delivery' },
    { value: 'available', label: 'Back at Warehouse', description: 'Returned and available for new tasks' }
  ];

  const statusColors = {
    'assigned_loading': 'bg-blue-100 text-blue-800 border-blue-200',
    'in_transit_to_project': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'at_project_site': 'bg-purple-100 text-purple-800 border-purple-200',
    'delivered': 'bg-green-100 text-green-800 border-green-200',
    'in_transit_to_warehouse': 'bg-orange-100 text-orange-800 border-orange-200',
    'available': 'bg-gray-100 text-gray-800 border-gray-200'
  };

  useEffect(() => {
    fetchDriverAssignments();
  }, []);

  const fetchDriverAssignments = async () => {
    try {
      setLoading(true);
      // Get current user's vehicle assignments
      const response = await axios.get(`${API_BASE_URL}/api/vehicle-assignments/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Filter for active assignments for current user
      const activeAssignments = response.data.filter(assignment => 
        assignment.status === 'assigned' && !assignment.actual_return_date
      );
      
      setAssignments(activeAssignments);
    } catch (error) {
      console.error('Error fetching driver assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateVehicleStatus = async (vehicleId, newStatus) => {
    try {
      await axios.post(`${API_BASE_URL}/api/vehicles/${vehicleId}/change-status/`, {
        status: newStatus,
        location: statusUpdateData.location,
        notes: statusUpdateData.notes
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      setShowStatusUpdate(false);
      setSelectedAssignment(null);
      setStatusUpdateData({ status: '', location: '', notes: '' });
      fetchDriverAssignments();
      alert('Status updated successfully!');
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  const openStatusUpdateModal = (assignment, status) => {
    setSelectedAssignment(assignment);
    setStatusUpdateData({
      status: status,
      location: '',
      notes: ''
    });
    setShowStatusUpdate(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-300 rounded w-3/4"></div>
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Navigation className="mr-3 h-6 w-6 text-blue-600" />
            Driver Dashboard
          </h1>
          <p className="text-gray-600 text-sm mt-1">Your current delivery tasks</p>
        </div>

        {/* No Assignments */}
        {assignments.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Assignments</h3>
            <p className="text-gray-600">You don't have any current delivery tasks.</p>
          </div>
        )}

        {/* Assignment Cards */}
        {assignments.map((assignment) => (
          <div key={assignment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden">
            {/* Assignment Header */}
            <div className="p-4 border-b border-gray-200 bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {assignment.car_details?.car_brand} {assignment.car_details?.car_model}
                  </h3>
                  <p className="text-sm text-gray-600">{assignment.car_details?.plate_number}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[assignment.car_details?.car_status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                  {assignment.car_details?.car_status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>

            {/* Assignment Details */}
            <div className="p-4 space-y-3">
              {/* Project Info */}
              {assignment.project_details && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{assignment.project_details.name}</p>
                    <p className="text-sm text-gray-600">Destination Project</p>
                  </div>
                </div>
              )}

              {/* Assignment Date */}
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-900">
                    Assigned: {new Date(assignment.assigned_date).toLocaleString()}
                  </p>
                  {assignment.expected_return_date && (
                    <p className="text-sm text-gray-600">
                      Expected return: {new Date(assignment.expected_return_date).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Assignment Notes */}
              {assignment.assignment_notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{assignment.assignment_notes}</p>
                </div>
              )}

              {/* Delivery Status */}
              {assignment.delivery_confirmed ? (
                <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Delivery Confirmed</p>
                    <p className="text-xs text-green-600">
                      by {assignment.delivery_confirmed_by_name} at {new Date(assignment.delivery_confirmed_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <p className="text-sm text-yellow-800">Awaiting delivery confirmation</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-2">
                {/* Status-specific actions */}
                {assignment.car_details?.car_status === 'assigned_loading' && (
                  <button
                    onClick={() => openStatusUpdateModal(assignment, 'in_transit_to_project')}
                    className="px-3 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    🚚 Leaving for Project
                  </button>
                )}
                
                {assignment.car_details?.car_status === 'in_transit_to_project' && (
                  <button
                    onClick={() => openStatusUpdateModal(assignment, 'at_project_site')}
                    className="px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    📍 Arrived at Project
                  </button>
                )}
                
                {(assignment.car_details?.car_status === 'delivered' || assignment.delivery_confirmed) && (
                  <button
                    onClick={() => openStatusUpdateModal(assignment, 'in_transit_to_warehouse')}
                    className="px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    🔄 Returning to Base
                  </button>
                )}
                
                {assignment.car_details?.car_status === 'in_transit_to_warehouse' && (
                  <button
                    onClick={() => openStatusUpdateModal(assignment, 'available')}
                    className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                  >
                    ✅ Back at Warehouse
                  </button>
                )}

                {/* Always available: Custom status update */}
                <button
                  onClick={() => setSelectedAssignment(assignment)}
                  className="px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                >
                  📱 Update Status
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Status Update Modal */}
        {showStatusUpdate && selectedAssignment && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-4 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Update Status
                </h3>
                
                <div className="space-y-4">
                  {/* Status Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Status</label>
                    <div className="space-y-2">
                      {statusOptions.map((option) => (
                        <label key={option.value} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            name="status"
                            value={option.value}
                            checked={statusUpdateData.status === option.value}
                            onChange={(e) => setStatusUpdateData({...statusUpdateData, status: e.target.value})}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{option.label}</div>
                            <div className="text-sm text-gray-600">{option.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Location</label>
                    <input
                      type="text"
                      value={statusUpdateData.location}
                      onChange={(e) => setStatusUpdateData({...statusUpdateData, location: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Where are you now?"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                    <textarea
                      value={statusUpdateData.notes}
                      onChange={(e) => setStatusUpdateData({...statusUpdateData, notes: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                      placeholder="Any additional information..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowStatusUpdate(false);
                      setSelectedAssignment(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => updateVehicleStatus(selectedAssignment.car, statusUpdateData.status)}
                    disabled={!statusUpdateData.status}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Update Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Custom Status Modal */}
        {selectedAssignment && !showStatusUpdate && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-4 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Quick Status Update
                </h3>
                
                <div className="space-y-2">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setStatusUpdateData({ status: option.value, location: '', notes: '' });
                        setShowStatusUpdate(true);
                      }}
                      className="w-full text-left p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-600">{option.description}</div>
                    </button>
                  ))}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setSelectedAssignment(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverInterface;